import { SITE_ENV_LABELS, Site } from '@pubstack/common/src/site'
import { Checkbox } from '@pubstack/ui/components/Checkbox'
import { FunctionComponent } from 'react'
import { MediaLink } from '~/feature-components/MediaLink'

import Table, { TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { PATH_BY_PRODUCT } from '~/hooks/useProduct'
import { DeploymentSelection, headerCheckboxState, isRowSelected } from './deploymentCenterSelection'

type Props = {
  medias: Site[]
  isLoading: boolean
  selection: DeploymentSelection
  onToggleRow: (siteId: string) => void
  onTogglePage: () => void
}

/**
 * The To configure tab, which is the one tab whose rows are **medias** rather than stack groups — a media
 * with no stack has no stack to point at.
 *
 * The old page fabricated a stack per media with `uniqueId()` purely to fit its site-shaped table, then
 * passed `disableSitesExpansion` to stop anyone opening the fake. A separate table is the smaller thing:
 * three columns, no expansion, and no invented ids in a selection keyed on them.
 *
 * Rows are Node's — the medias Node knows about and has no stack for. That is not a choice this table
 * makes but the shape of the only bulk creation that exists (`createDefaultStacks` is `pbs`-only), and it
 * is why the tab is shown only to a scope with a Node reason to see it.
 */
export const DeploymentCenterMediaTable: FunctionComponent<Props> = ({ medias, isLoading, selection, onToggleRow, onTogglePage }) => {
  const pageIds = medias.map((media) => media.id)
  const header = headerCheckboxState(selection, pageIds)

  const columns: TableColumns<Site> = [
    {
      name: (
        <Checkbox
          checked={header.checked}
          indeterminate={header.indeterminate}
          disabled={medias.length === 0}
          onChange={onTogglePage}
          trackingLabel={header.checked ? 'common-deployment-center-select-page-unset' : 'common-deployment-center-select-page-set'}
        />
      ),
      isSortable: false,
      width: '48px',
    },
    // Media carries no width: it is free text, and under fixed layout it takes what Environment leaves.
    { name: 'Media', isSortable: false },
    { name: 'Environment', isSortable: false, width: '160px' },
  ]

  return (
    // Widths, so the columns do not re-measure themselves against whichever names this page happens to
    // hold — see `Column['width']`. `noWrapHeader` because a header no longer widens its own column.
    <Table columns={columns} isLoading={isLoading} noWrapHeader>
      {medias.length === 0 ? (
        <TableRow>
          <TableCell colspan={columns.length}>No medias to configure.</TableCell>
        </TableRow>
      ) : (
        medias.map((media) => {
          const selected = isRowSelected(selection, media.id)
          const path = `${PATH_BY_PRODUCT.pbs}/sites/${media.id}`

          return (
            <TableRow key={media.id}>
              <TableCell>
                <Checkbox
                  checked={selected}
                  onChange={() => onToggleRow(media.id)}
                  trackingLabel={selected ? 'common-deployment-center-select-media-unset' : 'common-deployment-center-select-media-set'}
                />
              </TableCell>
              {/* Bold for the same reason as the stack table's: the checkbox is the first cell, so the
                  media does not inherit the weight `Table` gives one. */}
              <TableCell className={'font-bold break-words'}>
                <MediaLink name={media.name} path={path} trackingLabel={'common-deployment-center-media-navigate'} />
              </TableCell>
              <TableCell>{SITE_ENV_LABELS[media.env]}</TableCell>
            </TableRow>
          )
        })
      )}
    </Table>
  )
}
